import React from 'react';
import { TemplateSection } from '../../../../../redux/features/doctor/Dashboard/templates/templates';
import { useAppSelector } from '../../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { Paper, TextField, Box, IconButton } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

interface EditorSectionProps {
  section: TemplateSection;
  onUpdate: (updatedSection: TemplateSection) => void;
  onDelete: () => void;
}

const EditorSection: React.FC<EditorSectionProps> = ({ section, onUpdate, onDelete }) => {
  const { t } = useTranslation();
  const colors = useAppSelector((state) => state.theme.colors);

  const handleChange = (field: keyof Omit<TemplateSection, 'id'>, value: string) => {
    onUpdate({ ...section, [field]: value });
  };

  // FIX: Using the correct theme color 'listInfo' for all input labels.
  const inputStyles = {
    '& .MuiInput-input': { color: colors?.text },
    '& .MuiInput-underline:before': { borderBottomColor: colors?.folderDataRowBorderColor },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: colors?.lightBlue },
    '& .MuiInputLabel-root': { color: colors?.listInfo },
    '& .MuiInputLabel-root.Mui-focused': { color: colors?.lightBlue },
  };

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 2, 
        borderColor: colors?.folderDataRowBorderColor, 
        borderRadius: 3,
        backgroundColor: colors?.interface
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          label={t('sectionTitle')}
          value={section.title}
          onChange={(e) => handleChange('title', e.target.value)}
          fullWidth
          variant="standard"
          sx={{...inputStyles, '& .MuiInput-input': { ...inputStyles['& .MuiInput-input'], fontWeight: 'bold', fontSize: '1.1rem' } }}
        />
        <IconButton onClick={onDelete} size="small" sx={{ color: colors?.listInfo, '&:hover': { color: colors?.red }, ml: 2 }}>
          <DeleteForeverIcon />
        </IconButton>
      </Box>
      <TextField
        label={t('sectionDescription')}
        value={section.description}
        onChange={(e) => handleChange('description', e.target.value)}
        fullWidth
        multiline
        rows={2}
        variant="standard"
        sx={inputStyles}
      />
      <TextField
        label={t('sectionExampleText')}
        value={section.exampleText}
        onChange={(e) => handleChange('exampleText', e.target.value)}
        fullWidth
        multiline
        rows={4}
        variant="standard"
        sx={inputStyles}
      />
    </Paper>
  );
};

export default EditorSection;